import React, { useEffect, useState } from "react";
import { CardContent, Typography, Button, Modal, Box, Grid} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import CostStatusWorkflowStepper from "./MonthlyCostStatusSnapshotModalContent";
import MonthlyCostStatusSnapshotViewer from "./MonthlyCostStatusSnapshotModalViewer";
import { commitMonthlyCostStatusSnapshot, fetchProjectSettings } from "../../Containers/api";
import { setSettingsData, setViewMonthlyCostStatusSubmission, setViewMonthlyCostStatusEditor } from "../../../../store/slices/projectSlice";

// Case 0: Loading
// Case 2: Open window. Snapshot taken. Not yet submitted (Draft/Not Started).
// Case 3: Open window. Snapshot taken. Submitted.
// Case 4: Closed (past end of month). Admin can override via allow_after_close.
// Case 5: Monthly cost status not active for this project.
// Case 6: No snapshots taken for this month.
// Case 7: Fallback error state.

const useReduxSelectors = () => {
	const settingsData = useSelector((state) => state.project.settingsData);
	const isLoading = useSelector((state) => state.global.isLoading);
	const projectId = useSelector((state) => state.project.selectedProjectId);
	const projectData = useSelector((state) => state.project.projectData);
	const viewMonthlyCostStatusSubmission = useSelector((state) => state.project.viewMonthlyCostStatusSubmission);
	const viewMonthlyCostStatusEditor = useSelector((state) => state.project.viewMonthlyCostStatusEditor);
	const hubDomain = useSelector((state) => state.global.hubDomain);

	return {
		settingsData,
		isLoading,
		projectId,
		projectData,
		viewMonthlyCostStatusSubmission,
		viewMonthlyCostStatusEditor,
		hubDomain
	};
};

const MonthlyCostStatusSnapshotBox = () => {
	const {
		settingsData,
		projectId,
		isLoading,
		viewMonthlyCostStatusSubmission,
		viewMonthlyCostStatusEditor,
		hubDomain
	} = useReduxSelectors();

	// Grab and deconstruct monthy_cost_status_data from settingsData
	const monthlyCostStatusData = settingsData?.monthly_cost_status_data || {};
	const {
		current_month_snapshots = [],
		current_month_snapshots_count = 0,
		selected_snapshot = null,
		committed_snapshot = null,
		cost_status_status = null,
		allow_after_close = false,
		on_time_deadline = null,
		window_close = null,
	} = monthlyCostStatusData;

	// Deadline is anchored to Central (backend-emitted instants); render it in the
	// viewer's local time so non-Central users see when it closes for them. The 00:00
	// boundary instants are shown as the end of the prior day (11:59 PM) for readability.
	const isCentralViewer = Intl.DateTimeFormat().resolvedOptions().timeZone === 'America/Chicago';
	const fmtDeadline = (iso, timeZone) =>
		new Date(new Date(iso).getTime() - 60000).toLocaleString('en-US', {
			...(timeZone ? { timeZone } : {}),
			month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
		});
	// Only trust valid ISO instants from the backend; ignore malformed values.
	const parseInstant = (iso) => {
		const d = iso ? new Date(iso) : null;
		return d && !Number.isNaN(d.getTime()) ? d : null;
	};
	const windowCloseDate = parseInstant(window_close);
	const onTimeDate = parseInstant(on_time_deadline);
	// Absolute-instant comparison (tz-safe): are we past the on-time deadline?
	const isLateNow = onTimeDate ? Date.now() >= onTimeDate.getTime() : false;
	const deadlineCT = (iso) => `${fmtDeadline(iso, 'America/Chicago')} CT`;
	const deadlineLocal = (iso) => (isCentralViewer ? '' : ` (${fmtDeadline(iso)} your time)`);

	// Shown wherever the deadline is relevant: when submitting (case 2/3) and when a
	// snapshot still needs to be taken (case 6), so the closing time is visible either way.
	const deadlineBanner = windowCloseDate ? (
		<div style={{ marginBottom: '12px', padding: '8px 12px', borderRadius: '6px', border: `1px solid ${isLateNow ? '#fed7aa' : '#bfdbfe'}`, backgroundColor: isLateNow ? '#fff7ed' : '#eff6ff' }}>
			{isLateNow ? (
				<>
					<Typography className="titillium-web" style={{ fontSize: '13px', textAlign: 'center', color: '#9a3412' }}>
						The on-time deadline has passed. Submissions are now marked <strong>Submitted Late</strong>
					</Typography>
					<Typography className="titillium-web" style={{ fontSize: '12px', textAlign: 'center', color: '#9a3412', marginTop: '2px', opacity: 0.9 }}>
						Window closes {deadlineCT(window_close)}{deadlineLocal(window_close)}
					</Typography>
				</>
			) : (
				<>
					<Typography className="titillium-web" style={{ fontSize: '13px', textAlign: 'center', color: '#1e40af' }}>
						Window closes <strong>{deadlineCT(window_close)}</strong>{deadlineLocal(window_close)}
					</Typography>
					{onTimeDate && (
						<Typography className="titillium-web" style={{ fontSize: '12px', textAlign: 'center', color: '#1e40af', marginTop: '2px', opacity: 0.9 }}>
							On time through {deadlineCT(on_time_deadline)}{deadlineLocal(on_time_deadline)}
						</Typography>
					)}
				</>
			)}
		</div>
	) : null;

	const today = new Date();
	// const thisMonth = today.toLocaleString('default', { month: 'long' });
	const targetDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 0, 0, 0, 0);
	const countdown_days = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));

	const getCaseNumber = () => {
		// Ensure we have all required data before determining case
		// Check if settingsData is valid object (not array) AND has monthly_cost_status_data
		const hasValidData = projectId &&
			!isLoading &&
			settingsData &&
			typeof settingsData === 'object' &&
			!Array.isArray(settingsData) &&
			settingsData.monthly_cost_status_data;



		if (!hasValidData) {
			return 0;
		} else if (countdown_days < 0) {
			// Past end of month — period closed
			return 4;
		} else if (monthlyCostStatusData.monthly_cost_status_active === false) {
			// If monthly cost status is not active, set case 5
			return 5;
		} else if (current_month_snapshots_count === 0) {
			// No snapshots taken for this month, set case 6
			return 6;
		} else if (countdown_days >= 0) {
			// If within window and snapshot is committed set case 3, else case 2
			return (cost_status_status && (cost_status_status !== 'Not Started') && (cost_status_status !== 'Draft')) ? 3 : 2;
		} else {
			return 7; // Fallback case, should not happen
		}
	};

	const [caseNumber, setCaseNumber] = useState(0); // Start with loading state
	const [selectedSnapshotId, setSelectedSnapshotId] = useState(selected_snapshot?.id || null);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const dispatch = useDispatch();

	// Track previous projectId to detect project switches
	const [previousProjectId, setPreviousProjectId] = useState(projectId);

	// Separate useEffect specifically for project changes - runs first
	useEffect(() => {
		if (projectId !== previousProjectId) {
			// Immediately reset all state
			setCaseNumber(0);
			setIsInitialLoad(true);
			setSelectedSnapshotId(null);
			setPreviousProjectId(projectId);
		}
	}, [projectId]); // Only depend on projectId

	// Main data loading useEffect - only handles data state, not project changes
	useEffect(() => {
		// More robust check: settingsData should be an object (not array) with monthly_cost_status_data
		const hasValidSettingsData = settingsData &&
			typeof settingsData === 'object' &&
			!Array.isArray(settingsData) &&
			settingsData.monthly_cost_status_data;

		// Only proceed if we have actual monthly cost status data loaded and not in loading state
		if (!isLoading && projectId && hasValidSettingsData) {
			const newCaseNumber = getCaseNumber();
			setCaseNumber(newCaseNumber);
			setIsInitialLoad(false);
		} else if (isLoading || !projectId || !hasValidSettingsData) {
			// Stay in loading state if any required data is missing or still loading
			setCaseNumber(0);
			setIsInitialLoad(true);
		}
	}, [isLoading, settingsData]); // Removed projectId from deps since it's handled separately

	// Handle when no project is selected
	useEffect(() => {
		if (!projectId) {
			setCaseNumber(0);
			setIsInitialLoad(true);
			setSelectedSnapshotId(null);
		}
	}, [projectId]);

	// Refresh Selected Snapshot to display
	useEffect(() => {
		if (selected_snapshot?.id) {
			setSelectedSnapshotId(selected_snapshot.id);
		} else {
			setSelectedSnapshotId(null);
		}
	}, [selected_snapshot]);

	const handleCloseModal = () => {
		dispatch(setViewMonthlyCostStatusEditor(false));
	};

	// Handle Commit of Snapshots
	// const handleCommit = async () => {
	// 	if (!projectId || !selectedSnapshotId) {
	// 		toast.error("Missing projectId or snapshot selection.");
	// 		return;
	// 	}
	// 	toast.success("Committing the selected snapshot...");
	// 	handleCloseModal();
	// 	try {
	// 		// API Call Here
	// 		const resData = await commitMonthlyCostStatusSnapshot(projectId, Number(selectedSnapshotId));
	// 		if (resData.error) {
	// 			throw new Error(resData.error);
	// 		}
	// 		// If successful:
	// 		toast.success("Monthly Cost Status committed successfully!");

	// 		// Update Project Data
	// 		const updatedProjectSettings = await fetchProjectSettings(projectId);

	// 		// Dispatch to Redux to update settingsData
	// 		if (updatedProjectSettings) {
	// 			dispatch(setSettingsData(updatedProjectSettings));
	// 			toast.success("Project settings data refreshed!");
	// 		} else {
	// 			toast.error("Updated settings data not received from API.");
	// 		}
	// 	} catch (error) {
	// 		console.error("Error committing cost status:", error);
	// 		toast.error("Commit failed. Please try again or contact support.");
	// 	}
	// };

	return (
		<div>
			{(caseNumber === 0 || isInitialLoad) && (
				<CardContent style={{ paddingTop: '20px', position: 'relative' }}>
					<Typography variant="body1" className="titillium-web" style={{ textAlign: "center", marginBottom: "5px" }}>
						Loading Monthly Cost Status Data...
					</Typography>
				</CardContent>
			)}
			{[2, 3].includes(caseNumber) && (
				<CardContent style={{ paddingTop: '20px', position: 'relative' }}>
					<>
						{deadlineBanner}
						<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => {
									dispatch(setViewMonthlyCostStatusEditor(true));
								}}
								style={{
									backgroundColor: '#3b3b3bff',
									color: 'white',
									fontWeight: 'bold',
									textTransform: 'none',
									marginTop: '10px',
									marginBottom: '10px',
									width: caseNumber === 2 ? '100%' : '45%',
									borderRadius: '5px',
									padding: '10px 0',
									"&:hover": {
										backgroundColor: '#1b9fff',
										color: 'white',
									},
								}}
							>
								{caseNumber === 2 ? "Commit and Send Cost Status" : "Revise & Resend Cost Status"}
							</Button>
							{caseNumber === 3 && (
									<Button
										variant="contained"
										color="primary"
										onClick={() => dispatch(setViewMonthlyCostStatusSubmission(true))}
										style={{
											backgroundColor: '#3377a7',
											color: 'white',
											fontWeight: 'bold',
											textTransform: 'none',
											marginTop: '10px',
											marginBottom: '10px',
											width: '45%',
											borderRadius: '5px',
											padding: '10px 0',
											"&:hover": {
												backgroundColor: '#1b9fff',
												color: 'white',
											},
										}}
									>
										View Current Submission
									</Button>
							)}
						</div>
						{caseNumber === 3 && selected_snapshot && (
							<div
								className="titillium-web"
								style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
							>
								<br />
								<Typography className="titillium-web" style={{ textAlign: "center" }}>
									Current Committed Snapshot:
									<span style={{ fontWeight: 'bold' }}> {selected_snapshot.name}</span>
								</Typography>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'center',
										alignItems: 'center',
										marginTop: '5px'
									}}
								>
									<Typography className="titillium-web" style={{ fontSize: "14px"}}>
										Amount: $
										{monthlyCostStatusData?.selected_snapshot_data?.Amount?.toLocaleString() || "N/A"}
									</Typography>
									<Typography className="titillium-web" style={{ fontSize: "12px", margin: '0 5px' }}>
										|
									</Typography>
									<Typography className="titillium-web" style={{ fontSize: "14px"}}>
										Cost Status Profit: $
										{monthlyCostStatusData?.selected_snapshot_data?.["Cost Status Profit"]?.toLocaleString() || "N/A"}
									</Typography>
								</div>
							</div>
						)}
					</>
				</CardContent>
			)}
			{caseNumber === 4 && !allow_after_close && (
				<CardContent style={{ paddingTop: '20px', position: 'relative' }}>
					<Typography variant="body1" className="titillium-web" style={{ textAlign: "center", marginBottom: "5px" }}>
						The Cost Status period is currently closed.
						<br />
						It begins on the 1st of the coming month.
					</Typography>
				</CardContent>
			)}
			{caseNumber === 4 && allow_after_close && (
				<CardContent style={{ paddingTop: '20px', position: 'relative' }}>
					<Typography variant="body2" className="titillium-web" style={{ textAlign: "center", marginBottom: "8px", color: '#b45309', fontWeight: 'bold' }}>
						Late Submission — Admin Override
					</Typography>
					<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
						<Button
							variant="contained"
							color="primary"
							onClick={() => dispatch(setViewMonthlyCostStatusEditor(true))}
							style={{
								backgroundColor: '#b45309',
								color: 'white',
								fontWeight: 'bold',
								textTransform: 'none',
								marginBottom: '15px',
								width: '100%',
								borderRadius: '5px',
								padding: '10px 0',
							}}
						>
							Submit Late Cost Status
						</Button>
					</div>
				</CardContent>
			)}
			{caseNumber === 5 && (
				<CardContent style={{ paddingTop: '20px', position: 'relative' }}>
					<Typography variant="body1" className="titillium-web" style={{ textAlign: "center", marginBottom: "5px" }}>
						This project is not expecting Monthly Cost Status Reports.
						<br />
						If you would like to enable this feature, please update the project settings.
					</Typography>
				</CardContent>
			)}
			{caseNumber === 6 && (
				<CardContent style={{ paddingTop: '20px', position: 'relative' }}>
					{deadlineBanner}
					<Typography variant="body1" className="titillium-web" style={{ textAlign: "center", marginBottom: "5px" }}>
						No Project Type snapshots taken for this month.
						<br />
						Please create a snapshot in Procore to enable Monthly Cost Status Reports.
					</Typography>
				</CardContent>
			)}
			{caseNumber === 7 && (
				<CardContent style={{ paddingTop: '20px', position: 'relative' }}>
					<Typography variant="body1" className="titillium-web" style={{ textAlign: "center", marginBottom: "5px" }}>
						Something is off.
						<br />
						Please reach out to innovation@arcomurray.com.
					</Typography>
				</CardContent>
			)}

			{/* Modals — rendered independently of case number, controlled by Redux state */}
			<Modal
				open={viewMonthlyCostStatusEditor}
				onClose={handleCloseModal}
				aria-labelledby="snapshot-modal-title"
				aria-describedby="snapshot-modal-description"
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: '85vw',
						bgcolor: 'background.paper',
						boxShadow: 24,
						borderRadius: 2,
						maxHeight: '92vh',
						overflow: 'auto',
					}}
				>
					<div style={{ padding: '17px', color: 'white', backgroundColor: '#093d61' }}>
						<Grid container alignItems="center" justifyContent="space-between">
							<Grid item xs={9}>
								<Typography
									className='titillium-web-base'
									style={{ fontSize: '25px', fontWeight: 'bolder' }}
								>
									Monthly Cost Status Submission
								</Typography>
							</Grid>
						</Grid>
					</div>
					<div style={{ padding: '25px'}}>
						<CostStatusWorkflowStepper
							handleCloseModal={handleCloseModal}
							snapshots={current_month_snapshots}
						/>
					</div>
				</Box>
			</Modal>
			<Modal
				open={viewMonthlyCostStatusSubmission}
				onClose={() => dispatch(setViewMonthlyCostStatusSubmission(false))}
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: '70vw',
						bgcolor: 'background.paper',
						boxShadow: 24,
						borderRadius: 2,
						maxHeight: '80vh',
						overflow: 'auto',
					}}
				>
					<div style={{ padding: '25px', color: 'white', backgroundColor: '#093d61' }}>
						<Grid container alignItems="center" justifyContent="space-between">
							<Grid item xs={9}>
								<Typography
									className='titillium-web-base'
									style={{ fontSize: '25px', fontWeight: 'bolder', marginBottom: '5px' }}
								>
									Monthly Cost Status Submission - Viewer
								</Typography>
							</Grid>
						</Grid>
					</div>
					<div style={{ padding: '25px'}}>
						<MonthlyCostStatusSnapshotViewer
							projectId={projectId}
						/>
					</div>
				</Box>
			</Modal>
		</div>
	);
};

export default MonthlyCostStatusSnapshotBox;
